// Washington state-specific dangerous waste numbers: WAC 173-303-104.
// Washington designates by criteria (toxicity, persistence, corrosivity,
// PCB sources) beyond the federal RCRA baseline; WT01, WP01 and WP03
// designate as extremely hazardous waste (EHW).
// @source https://www.law.cornell.edu/regulations/washington/WAC-173-303-104
// @source https://app.leg.wa.gov/wac/default.aspx?cite=173-303-104
// @source WAC 173-303-100 (toxicity/persistence criteria)

export type WaCriteria = 'toxicity' | 'persistence' | 'corrosivity' | 'pcb';
export type WaDesignation = 'EHW' | 'DW';

export interface WaWasteCodeEntry {
  code: string;
  description: string;
  criteria: WaCriteria;
  designation: WaDesignation;
}

export const WA_CRITERIA_INFO: Record<WaCriteria, { title: string; blurb: string }> = {
  toxicity: {
    title: 'Toxicity Criteria',
    blurb: 'Book designation or bioassay: wastes designate by equivalent concentration of toxic constituents. At or above 1% equivalent concentration the waste is extremely hazardous (WT01); between 0.001% and 1% it is dangerous waste (WT02). WAC 173-303-100(5).',
  },
  persistence: {
    title: 'Persistence Criteria',
    blurb: 'Halogenated organic compounds (HOC) and polycyclic aromatic hydrocarbons (PAH) that resist degradation. HOC at 1% or more designates EHW (WP01), 0.01% to 1% designates DW (WP02); PAH at 1% or more designates EHW (WP03). WAC 173-303-100(6).',
  },
  corrosivity: {
    title: 'State-Only Corrosivity',
    blurb: 'Washington regulates solid corrosive wastes that the federal D002 characteristic (liquids only) does not capture.',
  },
  pcb: {
    title: 'State Source-Listed PCB Wastes',
    blurb: 'PCB wastes listed by Washington state sources under WAC 173-303-9904, beyond federal TSCA coverage.',
  },
};

export const WA_WASTE_CODES: WaWasteCodeEntry[] = [
  { code: 'WT01', description: 'Toxic dangerous waste designated as extremely hazardous waste (equivalent concentration >= 1%)', criteria: 'toxicity', designation: 'EHW' },
  { code: 'WT02', description: 'Toxic dangerous waste designated as dangerous waste (equivalent concentration below 1% but above 0.001%)', criteria: 'toxicity', designation: 'DW' },
  { code: 'WP01', description: 'Halogenated organic compound persistent dangerous waste, extremely hazardous waste category (HOC >= 1%)', criteria: 'persistence', designation: 'EHW' },
  { code: 'WP02', description: 'Halogenated organic compound persistent dangerous waste, dangerous waste category (HOC 0.01% to 1%)', criteria: 'persistence', designation: 'DW' },
  { code: 'WP03', description: 'Polycyclic aromatic hydrocarbon persistent dangerous waste, extremely hazardous waste category (PAH >= 1%)', criteria: 'persistence', designation: 'EHW' },
  { code: 'WSC2', description: 'State-only solid corrosive waste', criteria: 'corrosivity', designation: 'DW' },
  { code: 'WPCB', description: 'State source-listed PCB wastes per WAC 173-303-9904', criteria: 'pcb', designation: 'DW' },
];

const byCode = new Map(WA_WASTE_CODES.map((entry) => [entry.code, entry]));

export function getWaWasteCode(code: string): WaWasteCodeEntry | undefined {
  return byCode.get(code.trim().toUpperCase());
}
